/**
 * Guard for synthetic farm dec offers (#35226).
 *
 * tasks-be writes one offer per farm renewal (`<renewalPolicyId>-renewal-offer`), and the CFM
 * importer / migrated-policy backfill write the SAME-shaped offer for an imported base term
 * (`<basePolicyId>-renewal-offer`, #35438) — both purely so docs-engine can render that policy's
 * declaration. It is not a quote: it starts from an empty quote and carries only the fields the dec
 * pipeline reads, so `quote.offerings` is never set and the offer page dies on `PolicyPickerCard`'s
 * nested destructure of it.
 *
 * These records are no longer indexed, so they cannot be reached from offer search — but the
 * links staff have already shared still resolve, so a direct URL has to land somewhere sensible.
 * The policy is recoverable from the offer id alone, so the guard redirects without ever loading the
 * offer, and says what the record is rather than bouncing the user silently.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { SYNTHETIC_RENEWAL_OFFER_SUFFIX, policyIdFromSyntheticRenewalOfferId } from '@ourbranch/offer-utils';

import { useToast } from 'core/components/toast';

// Deliberately renewal-agnostic ("farm declaration", not "farm renewal declaration"): the same
// `-renewal-offer` id scheme now also backs imported BASE terms (#35438), which are not renewals, so
// tying the wording to renewals — or to import provenance, since `BaseFarmHandler.save` mints one on
// EVERY farm renewal, imported or not — would misdescribe the record for one case or the other.
export const SYNTHETIC_RENEWAL_OFFER_MESSAGE =
  'This is a synthetic offer record created to render a farm declaration, not a real quote. Redirecting you to the policy.';

/**
 * Matches ONLY offer ids ending in the synthetic suffix, so the route can sit ahead of the real
 * offer routes in the `<Switch>` without shadowing them. The route is non-exact, so `/details`
 * and the other sub-pages are guarded by this same entry.
 *
 * `[^/]+` rather than `.*`: an unbounded `.*` crosses `/`, so `/offer/foo/bar-renewal-offer/baz`
 * matched with an `offerId` of `foo/bar-renewal-offer` — an id no offer can have, and one
 * `isSyntheticRenewalOfferId` would never be asked about. Bounding the param to a single segment
 * keeps the route's notion of "synthetic renewal offer id" identical to the shared predicate's.
 */
export const SYNTHETIC_RENEWAL_OFFER_ROUTE_PATH = `/offer/:offerId([^/]+${SYNTHETIC_RENEWAL_OFFER_SUFFIX})`;

export const SyntheticRenewalOfferRedirect = ({ match }) => {
  const { notify } = useToast();
  const policyId = policyIdFromSyntheticRenewalOfferId(match?.params?.offerId);

  useEffect(() => {
    notify({ type: 'error', message: SYNTHETIC_RENEWAL_OFFER_MESSAGE, durationInSeconds: 10 });
  }, [notify]);

  // The account id is the policy id prefix, the same derivation the policy route itself uses.
  //
  // The `/search/offers` arm is unreachable via SYNTHETIC_RENEWAL_OFFER_ROUTE_PATH, which cannot
  // match an id the shared predicate rejects. It is kept so mounting this component under a looser
  // path degrades to offer search — what the offer page itself does when it cannot load an offer —
  // rather than redirecting to `/customer//policy/`.
  return <Redirect to={policyId ? `/customer/${policyId.split('-')[0]}/policy/${policyId}` : '/search/offers'} />;
};

SyntheticRenewalOfferRedirect.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      offerId: PropTypes.string
    })
  }).isRequired
};

export default SyntheticRenewalOfferRedirect;
